import React, {useLayoutEffect, useMemo} from 'react'
import WizSelectBox, {WSOption} from '@component/select/WizSelectBox'
import WizSegmentedControl from '@component/segmentedControl/WizSegmentedControl'
import {DEFAULT_COPY_LEADER_BOARD_FILTER, useCopyLeaderBoardFilter} from '@hook/useLeaderBoardFilter'
import useQueryGetExchanges from '@hook/query/core/exchange/useQueryGetExchanges'
import useLogin from '@hook/useLogin'
import {ExchangeType} from '@type/exchange'
import {createLog} from '@util/logs'
import {useTranslation} from '@lib/intl/useTranslations'
import {RankPeriod} from '@feature/leaderboard/pages/ScreenMain/MainLeaderboardHeaders/MainLeaderboardOptionContainer'

import dynamic from 'next/dynamic'
import SearchBar from '@feature/leaderboard/components/SearchBar/SearchBar'
import {useLoginModalStore} from '@store/LoginModalStore'

const FavoriteFilterButton = dynamic(
    () => import('@feature/leaderboard/components/FilterButton/FavoriteFilterButton'),
    {
        ssr: false,
    },
)

interface IProps {
    className?: string
}

const LeaderboardOptionContainer: React.FC<IProps> = ({className}) => {
    const t = useTranslation()

    const {copyFilter, setCopyFilter} = useCopyLeaderBoardFilter()
    const {exchange: filterExchange, period: filterPeriod, filter: isFilterFavorite} = copyFilter

    const {data: exchanges} = useQueryGetExchanges()
    const {isLogin} = useLogin()
    const showLoginModal = useLoginModalStore(state => state.showLoginModal)

    useLayoutEffect(() => {
        if (!isLogin()) {
            setCopyFilter({...DEFAULT_COPY_LEADER_BOARD_FILTER, filter: false})
        }
    }, [isLogin, setCopyFilter])

    const periodOptions = useMemo(() => {
        const list: WSOption[] = []
        list.push({id: '1', text: t('leaderboard.filter.button.24h')})
        list.push({id: '30', text: t('leaderboard.filter.button.30D')})
        list.push({id: '1000', text: t('leaderboard.filter.button.allTime')})
        return list
    }, [t])

    const exchangeOptions = useMemo(() => {
        const list: WSOption[] = [
            {id: 'all', text: t('leaderboard.filter.button.allExchanges'), icon: '/svgs/common/ic_exchanges_all.svg'},
        ]
        exchanges
            ?.filter(exchange => exchange.is_copybot_allowed)
            ?.map(item => list.push({id: item?.name, text: item?.display_name, icon: item?.icon}))
        return list
    }, [exchanges, t])

    const onChangeExchange = (exchange: ExchangeType | 'all') => {
        createLog('event', 'leaderboard_copy_leader_sort_exchange', {exchange: exchange})
        setCopyFilter({...copyFilter, exchange})
    }

    const onChangePeriod = (period: RankPeriod) => {
        createLog('event', 'leaderboard_copy_leader_sort_period', {period: period})
        setCopyFilter({...copyFilter, period})
    }

    const onChangeFilterFavorite = () => {
        if (!isLogin()) {
            showLoginModal()
            return
        }
        createLog('event', 'leaderboard_copy_leader_favorite_filter', {
            filter: isFilterFavorite ? 'unFavorite' : 'favorite',
        })

        if (!isFilterFavorite) {
            setCopyFilter({...DEFAULT_COPY_LEADER_BOARD_FILTER, filter: true})
            return
        }
        setCopyFilter({...copyFilter, filter: !isFilterFavorite})
    }
    return (
        <>
            <div className={'flex justify-between items-center md:hidden'}>
                <div className={'flex items-center gap-x-[10px]'}>
                    <FavoriteFilterButton
                        title={t('leader.favorite.button.title')}
                        isSelected={isFilterFavorite}
                        onClick={onChangeFilterFavorite}
                    />
                    <WizSelectBox
                        size={'small'}
                        className={'min-w-[180px]'}
                        optionClassName={'min-w-[180px]'}
                        selectedOption={filterExchange || exchangeOptions?.at(0)?.id}
                        options={exchangeOptions}
                        onChange={onChangeExchange}
                    />
                </div>
                <div className={'flex items-center gap-x-[10px]'}>
                    <WizSegmentedControl
                        selectedOption={filterPeriod || periodOptions?.at(-1)?.id}
                        options={periodOptions}
                        disabled={isFilterFavorite}
                        onChange={onChangePeriod}
                    />
                    <SearchBar />
                </div>
            </div>

            <div className={'hidden md:grid grid-cols-2 gap-[7px]'}>
                <div className={'flex flex-col'}>
                    <FavoriteFilterButton
                        title={t('leader.favorite.button.title')}
                        isSelected={isFilterFavorite}
                        onClick={onChangeFilterFavorite}
                    />
                </div>
                <div className={'flex items-center justify-end'}>
                    <SearchBar />
                </div>
                <div className={'flex flex-col'}>
                    <WizSelectBox
                        size={'small'}
                        className={'grow'}
                        selectedOption={filterExchange || exchangeOptions?.at(0)?.id}
                        options={exchangeOptions}
                        onChange={onChangeExchange}
                    />
                </div>
                <div className={'flex flex-col'}>
                    <WizSelectBox
                        size={'small'}
                        className={'grow flex-1'}
                        selectedOption={filterPeriod || periodOptions?.at(-1)?.id}
                        options={periodOptions}
                        onChange={onChangePeriod}
                    />
                </div>
            </div>
        </>
    )
}
export default React.memo(LeaderboardOptionContainer)
